import React, { useContext } from 'react';
import './product-offer.scss';
import inPost from 'assets/images/inpost-desktop.webp';
import inPostMobile from 'assets/images/inpost-mobile-small.svg';
import Stars from 'atomic/utilization/stars';
import flectionNumeral from 'helpers/number';
import Delivery from 'components/badges/delivery';
import Price from 'atomic/utilization/price';
import DealerOpinionsInterface, { BadgeType } from 'interfaces/dealer-opinions.interface';
import { TeApiContext } from '@ecommerce/nest-ssr/common';
import Tooltip from 'atomic/tooltip';
import Link from 'atomic/utilization/link';
import ProductOfferTooltip from 'components/product/product-offer/product-offer-dealer-opinions-tooltip';
import ProductMakeComment from 'components/product/product-make-report';
import { isProductRateZero } from 'helpers/product-rate-view-by-name';
import OfferAvailabilityInterface from 'interfaces/offer-availability.interface';
import DealerBadges from './components/dealer-badges';

interface ProductOfferProps {
    id: number;
    shopUrl: string;
    highlighted?: boolean;
    opinions: DealerOpinionsInterface | null;
    image: string;
    title: string;
    badges: {
        fastPayment?: boolean;
        isOpineoQuality?: boolean;
        isQuality?: boolean;
        inPost?: boolean;
        freeDelivery?: boolean;
        delivery24?: boolean;
        promoText?: string;
    };
    rates?: boolean;
    price: number;
    isOpen?: boolean;
    variantsCount?: number;
    dealer: {
        id: number;
        name: string;
        logo: string | null;
        authorized: {
            logo: string | null;
        };
        badge?: {
            name: string;
            type: BadgeType;
        };
    };
    availability: OfferAvailabilityInterface | null;
    toggleOpen: () => void;
    starsSize: string;
}

const ProductOffer: React.FC<ProductOfferProps> = props => {
    const api = useContext(TeApiContext);
    const gtmTag = props.availability
        ? props.availability.attributes
            .find((attr: { label: string; value: string; }) => attr.label === 'gtmTag')?.value
        : '';

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const isMobile = api.getDevice() === 'mobile';
    const shopUrl = `${props.shopUrl}&badge=1`;

    return (
        <div className={`product-offer gtm_or_bs ${props.highlighted ? 'gtm_bdg_h product-offer--highlighted' : ''}`}>
            <a
                href={shopUrl}
                className="product-offer__link gtm_or_row"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Idź do sklepu"
            />
            <div className="product-offer__logo">
                {props.dealer.logo
                    ? <img className="product-offer__logo__img" src={props.dealer.logo} alt={props.title}/>
                    : (
                        <div className="product-offer__logo__text">
                            {props.dealer.name}
                        </div>
                    )}
                {!isMobile && props.dealer.authorized.logo && (
                    <img
                        className="product-offer__logo-authorized"
                        src={props.dealer.authorized.logo}
                        alt={`${props.dealer.name} - autoryzowany sklep`}
                        width={85}
                        height={30}
                    />
                )}
            </div>
            <div className="product-offer__rating">
                <DealerBadges dealerBadge={props.dealer.badge} isOpineoQuality={props.badges.isOpineoQuality}/>
                <div className="gtm_stars">
                    {props.opinions?.count ? <Stars className={`${props.starsSize} stars star-rating--filled`} width={props.opinions.average}/> : ''}
                    {
                        props.opinions?.count
                            ? (
                                <span className="product-offer__rating-item">
                                    <Tooltip
                                        trigger={<div
                                            className="tooltip-trigger"
                                            role="button"
                                            tabIndex={0}
                                        >
                                            {isMobile
                                                ? `(${props.opinions.count})`
                                                : flectionNumeral(props.opinions.count, ['opinia', 'opinie', 'opinii'])}
                                        </div>}
                                        title={<>
                                        Ogólna ocena sklepu -
                                            {' '}
                                            <a className="dealer-link" href={`https://www.skapiec.pl/site/sklep/${props.dealer.id}#opinions`}>
                                                {props.dealer.name}
                                            </a>
                                        </>}
                                    >
                                        <ProductOfferTooltip opinions={props.opinions}/>
                                    </Tooltip>
                                </span>)
                            : 'Brak opinii z 12 miesięcy'
                    }
                </div>
            </div>
            <div className="product-offer__image">
                <img src={props.image} alt={props.title} loading="lazy"/>
            </div>
            <div className="product-offer__title">
                <span className="product-offer__title-text gtm_or_name">
                    {props.title}
                </span>
            </div>
            {props.badges.promoText && (
                <span className="product-offer__promo-text">
                    {props.badges.promoText}
                </span>)}
            <span className="product-offer__badges">
                <span className="product-offer__badges-availability" data-gtm={gtmTag}>
                    <i className="icon-clock"/>
                    {' '}
                    {props.availability ? props.availability.label : 'Sprawdź w sklepie'}
                </span>
                {props.badges.fastPayment && (
                    <span className="gtm_bdg_fp">
                        <i className="icon-credit-card"/>
                        {' '}
                        Szybkie płatności
                    </span>
                )}
                {props.rates && (
                    <span className="gtm_bdg_r">
                        <i className="icon-money"/>
                        {' '}
                        Raty
                        {isProductRateZero(props.title) ? ' 0%' : ''}
                    </span>
                )}
            </span>
            <span className="product-offer__extra">
                {props.variantsCount
                    ? (
                        <button
                            className="product-offer__extra-variants gtm_oa_ver"
                            type="button"
                            tabIndex={0}
                            onClick={(event): void => {
                                event.preventDefault();
                                event.stopPropagation();
                                props.toggleOpen();
                            }}
                        >
                            <i className={props.isOpen ? 'icon-arr1-up' : 'icon-arr1-down'}/>
                            {flectionNumeral(props.variantsCount, ['wariant', 'warianty', 'wariantów'])}
                        </button>
                    )
                    : ''}
                <ProductMakeComment offerId={props.id} dealerId={props.dealer.id} price={props.price}/>
            </span>
            <div className="product-offer__inpost">
                {props.badges.inPost && <img className="desktop" src={inPost} alt="InPost"
                    width={50} height={45} loading="lazy"
                />}
                {props.badges.inPost && <img className="mobile" src={inPostMobile} alt="InPost"
                    width={33} height={20} loading="lazy"
                />}
            </div>

            <Price price={props.price} weight="600" size="xl"
                color="dark" className="product-offer__price"
            />

            <Delivery freeDelivery={props.badges.freeDelivery} dealerId={props.dealer.id} className="product-offer__delivery gtm_bdg_fd"/>

            <span className="product-offer__availability" data-gtm={gtmTag}>
                <i className="icon-clock"/>
                {props.availability ? props.availability.label : 'Sprawdź w sklepie'}
            </span>

            <Link
                href={shopUrl}
                target="_blank"
                className="product-offer__button"
                type={'button'}
                color={'orange'}
                uppercase={true}
                block={true}
                rel="nofollow noopener noreferrer"
                ariaLabel="Idź do sklepu"
            >
                <div className="text gtm_or_button">
                    Idź do sklepu
                </div>
                <i className="icon icon-arr2-right"/>
                <Price className="price gtm_or_price" price={props.price} weight="400"
                    size="xl" color="white"
                />
            </Link>
        </div>
    );
};

export default ProductOffer;
